import { Box, Button, CircularProgress, Modal, Stack, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import ModalV2 from '/@/components/bayer/modal-v2';
import Spinner from '/@/components/spinner';
import { useGetUserNode, useOwnUserNode, useSendFieldviewInvitation } from '/@/hooks/nodes';
import { useCreateOrUpdateCropMonitoringResult } from '/@/hooks/reports';
import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import { linkFieldviewAccount } from '/@/store/session/actions';
import type { FieldRegistryStep } from '/@/store/tracking/events/field-registry';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { reverse } from '/@/utils/router';
import {
    errorDetail,
    flowEventProps,
    harvestInvalidFields,
    harvestPrefill,
    trackFormAbandoned,
    trackFormOpened,
    useFieldRegistryFlow,
    useFormExitTracking,
} from '../detail/registries/components/add/flow-tracking';
import FieldviewReportsErrorModal from './components/fieldview-reports-error';
import CreateReportForm from './components/form';
import useFieldviewReports from './hooks/use-fieldview-reports';
import { useReportForm } from './hooks/use-report-form';

const CreateReportModal = ({
    field,
    cropMonitoring,
    isOpen,
    setIsOpen,
}: {
    field: Field;
    cropMonitoring: CropMonitoring;
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const fieldIsFieldview = field.externalProvider === 'fieldview';
    const isBayerUserType = useIsValidatedBayerUserType();
    const ownUserQuery = useOwnUserNode();
    const isOwnField = field.userNode === ownUserQuery.data?.id;
    const fieldOwnerNode = useGetUserNode(field.userNode);
    const sendFieldviewInvitation = useSendFieldviewInvitation();
    const showRequestAuth = !isOwnField && isBayerUserType;

    const [manualMode, setManualMode] = useState(false);
    const [showInvitationScreen, setShowInvitationScreen] = useState(false);

    // The attempt "Agregar registro" started, when the harvest comes from there (CUL-1632)
    const flow = useFieldRegistryFlow();
    const flowProps = flow ? flowEventProps(flow) : {};

    const fieldviewReports = useFieldviewReports({ field });
    const { createReports } = fieldviewReports;
    // When the current FieldView lookup began; a resync starts another one.
    const lookupStartedAt = useRef<number | undefined>(undefined);
    const lookupSeenPending = useRef(false);
    const startLookup = () => {
        lookupStartedAt.current = Date.now();
        lookupSeenPending.current = false;
        createReports();
    };
    // Only fetch/generate FieldView reports once the modal is actually opened —
    // the modal can be mounted (e.g. on the detail page) while closed, and
    // create-all triggers backend sync/auto-harvest, so it must not run on mount.
    // biome-ignore lint/correctness/useExhaustiveDependencies: `startLookup` only wraps `createReports`
    useEffect(() => {
        if (isOpen && fieldIsFieldview) startLookup();
    }, [isOpen, fieldIsFieldview, createReports]);

    const reportForm = useReportForm({
        field,
        cropMonitoring,
        isFieldviewType: fieldIsFieldview,
        enabled: isOpen && fieldIsFieldview,
        reportsMutating: fieldviewReports.isMutating,
        reportsError: fieldviewReports.error,
    });
    const { form } = reportForm;

    const createOrUpdateCropMonitoringResult = useCreateOrUpdateCropMonitoringResult({
        cropMonitoringId: cropMonitoring?.id,
        fieldId: field.id,
    });

    // Once per lookup, when its result is in: the reports were created and fetched, or failed.
    // A lookup only counts as done after it was seen pending, so a cached result from an earlier
    // open doesn't end it before it starts.
    // biome-ignore lint/correctness/useExhaustiveDependencies: `flowProps` is read when the lookup ends
    useEffect(() => {
        if (lookupStartedAt.current === undefined) return;
        if (reportForm.reportsPending) {
            lookupSeenPending.current = true;
            return;
        }
        if (!lookupSeenPending.current || !reportForm.harvestDataResolved) return;
        const durationMs = Date.now() - lookupStartedAt.current;
        lookupStartedAt.current = undefined;
        mpTrackEvent('Harvest report FieldView lookup', {
            ...flowProps,
            result: fieldviewReports.error ? 'error' : reportForm.hasHarvestData ? 'found' : 'no_data',
            duration_ms: durationMs,
        });
    }, [reportForm.reportsPending, reportForm.harvestDataResolved]);

    const hasError = fieldIsFieldview && Boolean(fieldviewReports.error);
    const hasNoData = fieldIsFieldview && reportForm.harvestDataResolved && !reportForm.hasHarvestData;
    // While FieldView reports are still being fetched/generated we show the
    // loading screen instead of the (empty) form, so the modal never flashes
    // form → error.
    const fetchPending = isOpen && fieldIsFieldview && reportForm.reportsPending;
    const showErrorModal = isOpen && !manualMode && (hasError || hasNoData);
    const showForm = isOpen && !showErrorModal;

    // Where the user is, for the registry's events: the lookup, its error or no-data screen, or the form
    let step: FieldRegistryStep = 'form';
    if (fetchPending) step = 'loading';
    else if (showErrorModal) step = hasError ? 'fieldview_error' : 'fieldview_no_data';

    // The modal stays mounted while closed, so the exit tracking restarts on each open and only
    // reports while it is open.
    const tracked = useRef({ isOpen, step, touched: false });
    tracked.current = { isOpen, step, touched: form.formState.isDirty };
    const { end, finish, restart } = useFormExitTracking((exitMethod) => {
        if (flow && tracked.current.isOpen)
            trackFormAbandoned(flow, 'harvest', exitMethod, tracked.current.touched, tracked.current.step);
    });
    const formOpened = useRef(false);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `restart` is stable
    useEffect(() => {
        if (isOpen) {
            restart();
            formOpened.current = false;
        }
    }, [isOpen]);
    // Once per open, when the form shows: after the FieldView lookup, with what it prefilled
    // biome-ignore lint/correctness/useExhaustiveDependencies: the form's values are read when it shows
    useEffect(() => {
        if (!flow || !isOpen || step !== 'form' || formOpened.current) return;
        formOpened.current = true;
        trackFormOpened(flow, 'harvest', { step: 'form', prefilled: harvestPrefill(form.getValues()) });
    }, [flow, isOpen, step]);

    const trackFallback = (action: 'close' | 'load_manually' | 'resync' | 'request_auth') =>
        mpTrackEvent('Harvest report FieldView fallback', {
            ...flowProps,
            variant: hasError ? 'error' : 'no_data',
            action,
        });

    const title = (() => {
        if (fieldIsFieldview && reportForm.harvestAutoSelected) return _('Datos de cosecha encontrados en FieldView');
        return _('Completá los datos de la cosecha');
    })();

    const handleResync = () => {
        dispatch(
            linkFieldviewAccount({
                source: 'field-reports',
                onSuccess: () => {
                    setManualMode(false);
                    startLookup();
                },
            })
        );
    };

    const handleRequestAuth = async () => {
        const invitee = fieldOwnerNode.data;
        if (!invitee?.email) return;
        await sendFieldviewInvitation.trigger({
            invitedUserName: invitee.name || invitee.email,
            invitedUserEmail: invitee.email,
            sharedNodeId: invitee.id,
        });
        setShowInvitationScreen(true);
    };

    const submitDisabled = createOrUpdateCropMonitoringResult.isMutating || fetchPending;

    return (
        <>
            <FieldviewReportsErrorModal
                open={showErrorModal}
                variant={hasError ? 'error' : 'no-data'}
                isOwnField={isOwnField}
                showRequestAuth={showRequestAuth}
                showInvitationScreen={showInvitationScreen}
                userNodeId={field.userNode}
                requestAuthMutating={sendFieldviewInvitation.isMutating}
                onClose={() => {
                    trackFallback('close');
                    end('close');
                    setIsOpen(false);
                }}
                onLoadManually={() => {
                    trackFallback('load_manually');
                    setManualMode(true);
                }}
                onResync={() => {
                    trackFallback('resync');
                    handleResync();
                }}
                onRequestAuth={() => {
                    trackFallback('request_auth');
                    handleRequestAuth();
                }}
                onInvitationAck={() => setShowInvitationScreen(false)}
            />

            <Modal
                slotProps={{ backdrop: { sx: { backgroundColor: 'rgb(255 255 255)' } } }}
                open={createOrUpdateCropMonitoringResult.isMutating}
            >
                <Stack spacing={4} alignItems='center' justifyContent='center' height='100%'>
                    <Typography color='primary' variant='h5'>
                        {_('Generando reporte...')}
                    </Typography>
                    <Box sx={{ color: 'primary.onSurface' }}>
                        <CircularProgress color='inherit' size={75} />
                    </Box>
                    <Typography color='primary.mui.inactive' variant='body2'>
                        {_('Vas a poder ver cómo fue tu campaña y definir los próximos pasos con más datos.')}
                    </Typography>
                </Stack>
            </Modal>

            <ModalV2
                open={showForm}
                onClose={() => {
                    end('close');
                    setIsOpen(false);
                }}
                title={title}
                actions={
                    <>
                        <Button
                            variant='text'
                            color='secondary'
                            onClick={() => {
                                end('cancel');
                                setIsOpen(false);
                                form.reset();
                            }}
                            disabled={createOrUpdateCropMonitoringResult.isMutating}
                        >
                            {_('Cancelar')}
                        </Button>
                        <Button
                            type='submit'
                            variant='contained'
                            color='secondary'
                            disabled={submitDisabled}
                            startIcon={submitDisabled ? <CircularProgress size={20} /> : undefined}
                        >
                            {_('Generar reporte')}
                        </Button>
                    </>
                }
                actionsProps={{ sx: { justifyContent: 'end' } }}
                maxWidth='sm'
                containerProps={{
                    component: 'form',
                    onSubmit: form.handleSubmit(
                        async (data) => {
                            try {
                                await createOrUpdateCropMonitoringResult.trigger({
                                    harvestReport: data.harvestRequestId || undefined,
                                    harvestResult: data.harvestResultId || undefined,
                                    harvestDate: data.harvestDate?.format('YYYY-MM-DD'),
                                    averageYield: data.yield ?? undefined,
                                    humidityAtHarvest: data.humidity ? Number(data.humidity) : undefined,
                                });
                            } catch (error) {
                                if (flow)
                                    mpTrackEvent('Field Registry Save Failed', {
                                        ...flowEventProps(flow),
                                        registry_type: 'harvest',
                                        origin: flow.origin,
                                        error_detail: errorDetail(error),
                                        partial: false,
                                        step: 'form',
                                    });
                                // The form stays open with what was entered, so the user can retry
                                enqueueSnackbar(_('No se pudo generar el reporte. Intentá nuevamente.'), {
                                    variant: 'error',
                                });
                                return;
                            }
                            finish();
                            if (flow)
                                mpTrackEvent('Field Registry Saved', {
                                    ...flowEventProps(flow),
                                    registry_type: 'harvest',
                                    origin: flow.origin,
                                    entry_source: flow.entrySource,
                                    step: 'form',
                                });
                            setIsOpen(false);
                            navigate(
                                reverse('cropMonitoring.CornCropMonitoringDetail.report', {
                                    params: { cropMonitoringId: cropMonitoring.id },
                                })
                            );
                        },
                        (errors) => {
                            if (flow)
                                mpTrackEvent('Field Registry Submit Attempted', {
                                    ...flowEventProps(flow),
                                    registry_type: 'harvest',
                                    invalid_fields: harvestInvalidFields(Object.keys(errors)),
                                    step: 'form',
                                });
                        }
                    ),
                }}
            >
                {fetchPending ? (
                    <Spinner />
                ) : (
                    <CreateReportForm
                        cropMonitoring={cropMonitoring}
                        field={field}
                        form={form}
                        units={reportForm.units}
                        harvestDateOptions={reportForm.harvestDateOptions}
                        harvestAutoSelected={reportForm.harvestAutoSelected}
                    />
                )}
            </ModalV2>
        </>
    );
};

export default CreateReportModal;
